import {fromJS, Map} from 'immutable';
import influencerActions from '../actions/influencer.actions';
import dieselReducer from 'epic-diesel-common/src/decorators/DieselReducer';
import {canRemoveChannel} from '../utils/ValidationUtils';
import {Affiliate, CodeAvailability, FinalPayout, SocialPlatform} from '../../shared/types';

export interface InfluencerState {
    signup: boolean;
    loading: boolean;
    verificationSent: boolean;
    affiliateAccount: Affiliate;
    createAffiliateError: boolean | string;
    disconnectedPlatform: SocialPlatform;
    lastSocialUpdate: number | string | Date;
    finalPayout: FinalPayout;
    codeAvailability: CodeAvailability & {
        loading: boolean;
    };
}

export const DEFAULT_STATE = fromJS({
    signup: false,
    loading: false,
    verificationSent: false,
    affiliateAccount: {},
    createAffiliateError: null,
    disconnectedPlatform: null,
    lastSocialUpdate: null,
    finalPayout: {
        amount: 0,
        vBucks: 0
    },
    codeAvailability: {
        loading: false,
        slug: null,
        available: false,
        taken: false
    }
});

export const influencerReducer = (
    state: Map<keyof InfluencerState, any> = DEFAULT_STATE,
    action
) => {
    let newState = state;
    switch (action.type) {
        case influencerActions.TYPES.GET_INFLUENCER_PROFILE: {
            newState = newState.updateIn(['loading'], () => true);
            return newState;
        }
        case influencerActions.TYPES.GET_INFLUENCER_PROFILE_SUCCESS: {
            newState = newState.updateIn(['loading'], () => false);
            newState = newState.mergeIn(['affiliateAccount'], action.payload.data);
            return newState;
        }
        case influencerActions.TYPES.GET_INFLUENCER_PROFILE_FAILED: {
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }

        case influencerActions.TYPES.CREATE_INFLUENCER_PROFILE:
        case influencerActions.TYPES.REACTIVATE_INFLUENCER_PROFILE: {
            return newState.updateIn(['loading'], () => true);
        }
        case influencerActions.TYPES.CREATE_INFLUENCER_PROFILE_SUCCESS:
        case influencerActions.TYPES.REACTIVATE_INFLUENCER_PROFILE_SUCCESS: {
            return newState.merge({
                loading: false,
                affiliateAccount: action.payload.data,
                createAffiliateError: DEFAULT_STATE.createAffiliateError
            });
        }
        case influencerActions.TYPES.CREATE_INFLUENCER_PROFILE_FAILED:
        case influencerActions.TYPES.REACTIVATE_INFLUENCER_PROFILE_FAILED: {
            // to-do: look into payload.error response value (does it exist?)
            const {error = {}} = action.payload || {};
            const {status, data = {}} = error.response || {};

            switch (status) {
                case 401: {
                    newState = DEFAULT_STATE;
                    break;
                }
                case 400: {
                    newState = newState.updateIn(['loading'], () => false);
                    newState = newState.updateIn(
                        ['createAffiliateError'],
                        () => data.message || null
                    );
                    break;
                }
                default: {
                    newState = newState.updateIn(['loading'], () => false);
                    break;
                }
            }

            return newState;
        }
        case influencerActions.TYPES.RESEND_EMAIL_VERIFICATION: {
            newState = newState.updateIn(['loading'], () => true);
            newState = newState.updateIn(['verificationSent'], () => true);
            return newState;
        }
        case influencerActions.TYPES.RESEND_EMAIL_VERIFICATION_SUCCESS: {
            newState = newState.updateIn(['loading'], () => false);
            newState = newState.updateIn(['signup'], () => true);
            return newState;
        }
        case influencerActions.TYPES.RESEND_EMAIL_VERIFICATION_FAILED: {
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }

        case influencerActions.TYPES.GET_AFFILIATE_SOCIAL_CHANNELS: {
            return newState.updateIn(['loading'], () => false);
        }
        case influencerActions.TYPES.GET_AFFILIATE_SOCIAL_CHANNELS_SUCCESS: {
            const {data} = action.payload || {};
            const {socialChannels = [], oauthViolation} = data || {};
            return newState.merge({
                affiliateAccount: {
                    ...newState.get('affiliateAccount').toJS(),
                    socialChannels,
                    oauthViolation
                },
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }
        case influencerActions.TYPES.GET_AFFILIATE_SOCIAL_CHANNELS_FAILED: {
            return newState.merge({
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }

        case influencerActions.TYPES.UPDATE_OAUTH_CHANNEL: {
            return state.updateIn(['loading'], () => true);
        }

        case influencerActions.TYPES.UPDATE_OAUTH_CHANNEL_COMPLETE: {
            const {data} = action.payload || {};
            const {socialChannels = []} = data || {};
            if (socialChannels.length) {
                newState = newState.updateIn(
                    ['affiliateAccount', 'socialChannels'],
                    () => socialChannels
                );

                const mainChannel = socialChannels.find(sch => sch.mainChannel);
                if (mainChannel && (!mainChannel.violations || !mainChannel.violations.length)) {
                    newState = newState.updateIn(
                        ['affiliateAccount', 'oauthViolation'],
                        () => false
                    );
                }
            }

            return newState.merge({
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }

        case influencerActions.TYPES.UPDATE_OAUTH_CHANNEL_FAILED: {
            return state.merge({
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }

        case influencerActions.TYPES.UPDATE_AFFILIATE_MAIN_CHANNEL: {
            newState = newState.updateIn(['loading'], () => true);
            return newState;
        }

        case influencerActions.TYPES.UPDATE_AFFILIATE_MAIN_CHANNEL_SUCCESS: {
            const {data} = action.payload || {};
            const {socialChannels = [], oauthViolation} = data || {};

            if (socialChannels.length) {
                newState = newState.updateIn(
                    ['affiliateAccount', 'socialChannels'],
                    () => socialChannels
                );
            }

            newState = newState.updateIn(
                ['affiliateAccount', 'oauthViolation'],
                () => oauthViolation
            );

            return newState.merge({
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }

        case influencerActions.TYPES.UPDATE_AFFILIATE_MAIN_CHANNEL_FAILED: {
            return newState.merge({
                lastSocialUpdate: Date.now(),
                loading: false
            });
        }

        case influencerActions.TYPES.DISCONNECT_AFFILIATE_SOCIAL_CHANNEL: {
            newState = newState.updateIn(['loading'], () => true);
            return newState;
        }
        case influencerActions.TYPES.DISCONNECT_AFFILIATE_SOCIAL_CHANNEL_SUCCESS: {
            if (action.payload) {
                const {affiliateAccount} = newState.toJS();
                const {disconnected = false, platform, severConnection = false} = action.payload;

                if (disconnected) {
                    const {socialChannels = []} = affiliateAccount || {};
                    const foundChannelIndex = socialChannels.findIndex(
                        ch => ch.platform === platform
                    );

                    if (foundChannelIndex !== -1) {
                        // allow new applicants to remove
                        const channel = socialChannels[foundChannelIndex] || {};

                        if (!severConnection && canRemoveChannel(affiliateAccount, channel)) {
                            socialChannels.splice(foundChannelIndex, 1);
                        } else {
                            const {oauthDetails = {}} = socialChannels[foundChannelIndex] || {};
                            Object.assign(socialChannels[foundChannelIndex], {
                                channelStatus: 'NONE',
                                oauthDetails: {
                                    ...oauthDetails,
                                    valid: false
                                }
                            });
                        }

                        newState = newState.updateIn(
                            ['affiliateAccount', 'socialChannels'],
                            () => socialChannels
                        );
                    }
                    newState = newState.updateIn(['disconnectedPlatform'], () => platform);
                    newState = newState.updateIn(['severConnection'], () => severConnection);
                }
            }
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }
        case influencerActions.TYPES.DISCONNECT_AFFILIATE_SOCIAL_CHANNEL_FAILED: {
            newState = newState.updateIn(['disconnectedPlatform'], () => null);
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }

        case influencerActions.TYPES.UPDATE_AFFILIATE_PROFILE: {
            newState = newState.updateIn(['disconnectedPlatform'], () => null);
            newState = newState.mergeIn(['affiliateAccount'], action.payload.data);
            return newState;
        }

        case influencerActions.TYPES.RESET_DISCONNECTED_PLATFORM: {
            if (action.payload.platform === newState.toJS().disconnectedPlatform) {
                newState = newState.updateIn(['disconnectedPlatform'], () => null);
                newState = newState.updateIn(['severConnection'], () => null);
            }
            return newState;
        }

        case influencerActions.TYPES.ACCEPT_AGREEMENT:
        case influencerActions.TYPES.CLOSE_ACCOUNT: {
            return newState.updateIn(['loading'], () => true);
        }
        case influencerActions.TYPES.ACCEPT_AGREEMENT_SUCCESS:
        case influencerActions.TYPES.CLOSE_ACCOUNT_SUCCESS: {
            newState = newState.updateIn(['loading'], () => false);
            newState = newState.mergeIn(['affiliateAccount'], action.payload.data);
            return newState;
        }
        case influencerActions.TYPES.ACCEPT_AGREEMENT_FAILED:
        case influencerActions.TYPES.CLOSE_ACCOUNT_FAILED: {
            return newState.updateIn(['loading'], () => false);
        }

        case influencerActions.TYPES.GET_FINAL_PAYOUT: {
            newState = newState.updateIn(['loading'], () => true);
            return newState;
        }
        case influencerActions.TYPES.GET_FINAL_PAYOUT_SUCCESS: {
            if (action.payload.data) {
                newState = newState.updateIn(['finalPayout'], () => action.payload.data);
            }
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }
        case influencerActions.TYPES.GET_FINAL_PAYOUT_FAILED: {
            newState = newState.updateIn(['loading'], () => false);
            return newState;
        }

        case influencerActions.TYPES.CHECK_CODE_AVAILABILITY: {
            newState = newState.updateIn(['codeAvailability', 'loading'], () => true);
            return newState;
        }
        case influencerActions.TYPES.CHECK_CODE_AVAILABILITY_SUCCESS: {
            return newState.merge({
                codeAvailability: {
                    loading: false,
                    ...action.payload.data
                }
            });
        }
        case influencerActions.TYPES.CHECK_CODE_AVAILABILITY_FAILED: {
            newState = newState.updateIn(['codeAvailability', 'loading'], () => false);
            return newState;
        }

        default:
            return state;
    }
};

export default dieselReducer('influencer', influencerReducer);
